import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import Logo from "./Logo";
import CurrencySelector from "./CurrencySelector";
import { track } from "../lib/api";

const LINKS = [
  { label: "HOME", to: "/" },
  { label: "WHAT WE BUILD", to: "/#what-we-build" },
  { label: "STANDARD", to: "/standard" },
  { label: "RUSH HOUR", to: "/rush-hour" },
  { label: "HOW IT WORKS", to: "/#how-it-works" },
  { label: "PRICING", to: "/pricing" },
  { label: "CASE STUDIES", to: "/case-studies" },
  { label: "ABOUT", to: "/about" },
  { label: "CONTACT", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (to) => (e) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-header" data-testid="main-nav">
      <div className="max-w-[1760px] mx-auto px-5 sm:px-8 flex items-center justify-between gap-6 h-16 lg:h-20">
        <Link to="/" aria-label="Built2Last.live home" className="shrink-0"><Logo /></Link>

        <nav className="hidden min-[1500px]:flex items-center gap-4 flex-1 justify-center min-w-0">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={go(l.to)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[10px] font-semibold tracking-[0.14em] text-slate-300 hover:text-flame transition-colors duration-200 whitespace-nowrap"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden min-[1500px]:flex items-center gap-3 shrink-0">
          <CurrencySelector />
          <Button
            asChild
            variant="outline"
            className="border-flame/50 text-flame hover:bg-flame/10 hover:text-flame rounded-full px-5 font-semibold tracking-wide whitespace-nowrap"
          >
            <Link to="/book-rush-hour" data-testid="nav-book-rush-hour-btn" onClick={() => track("rush_hour_click", { location: "nav" })}>
              <Zap className="w-4 h-4" /> BOOK RUSH HOUR
            </Link>
          </Button>
          <Button
            asChild
            className="bg-flame hover:bg-flame-hover text-white rounded-full px-5 font-semibold tracking-wide shadow-[0_0_24px_rgba(249,115,22,0.35)] whitespace-nowrap"
          >
            <Link to="/get-estimate" data-testid="nav-get-estimate-btn" onClick={() => track("estimate_click", { location: "nav" })}>
              GET AN ESTIMATE
            </Link>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="nav-mobile-menu-btn" className="min-[1500px]:hidden text-slate-200 p-2 shrink-0" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-navy-800 border-white/10 w-80 overflow-y-auto">
            <div className="flex flex-col gap-5 mt-10">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={go(l.to)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm font-semibold tracking-[0.15em] text-slate-200 hover:text-flame transition-colors"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/live-build" onClick={() => setOpen(false)} data-testid="mobile-nav-live-build" className="text-sm font-semibold tracking-[0.15em] text-slate-200 hover:text-flame transition-colors">
                LIVE BUILD
              </Link>
              <Link to="/checkout" onClick={() => setOpen(false)} data-testid="mobile-nav-checkout" className="text-sm font-semibold tracking-[0.15em] text-slate-200 hover:text-flame transition-colors">
                CHECKOUT
              </Link>
              <div className="pt-2">
                <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500 mb-2">CURRENCY</p>
                <CurrencySelector testid="mobile-currency-selector" full />
              </div>
              <div className="pt-4 flex flex-col gap-3">
                <Button asChild className="bg-flame hover:bg-flame-hover text-white rounded-full font-semibold">
                  <Link to="/get-estimate" data-testid="mobile-nav-get-estimate-btn" onClick={() => setOpen(false)}>GET AN ESTIMATE</Link>
                </Button>
                <Button asChild variant="outline" className="border-flame/50 text-flame hover:bg-flame/10 rounded-full font-semibold">
                  <Link to="/book-rush-hour" data-testid="mobile-nav-book-rush-btn" onClick={() => setOpen(false)}>BOOK RUSH HOUR</Link>
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
